import {
    type Filter,
    type FilterConfig,
    FilterOperatorSchema,
    FilterValueSchema,
    getOperatorsForFieldType,
} from '@client/components/filters';
import { z } from 'zod';

/**
 * CUR-706 — the compliance-audit list's URL filter parameter, in two halves.
 *
 * A filter set may be supplied in the URL as a one-shot entry instruction; the shared
 * filter bar consumes it on mount and strips it back out (`useFilterBuilderWithUrl`),
 * and reload persistence comes from session path state, not from the link. See
 * `openspec/specs/compliance-audit-search-page/spec.md` — the address bar deliberately
 * does not mirror the active filters.
 *
 * The promise both halves exist to keep: a stale or hand-edited link degrades to an
 * unfiltered list, never to a crash and never to a PARTIALLY applied set — a link that
 * silently loses one of its filters shows a narrower result than it asked for, with
 * nothing on screen saying so.
 *
 * Lives beside the route rather than in it because a route module may only export its
 * `Route` at runtime, and both halves are worth testing directly.
 */

/** Ids with a dedicated variant below; the fallback must not also accept them. */
const RESERVED_FILTER_IDS = ['auditor', 'template'] as const;

/**
 * The SHAPE half. Every variant has to be bounded for `.catch(undefined)` to mean
 * anything: a permissive `value: z.any()` catch-all would swallow the very failures the
 * `.catch` exists to absorb and hand `buildWhereClause` a value it cannot build from
 * (`{ filterId: 'search', value: {} }` reaching `.trim()`, a non-numeric `template` id
 * reaching the server). So the two id-typed branches pin their operators and coerce
 * their ids back to bigint (a filter value makes the round trip through the URL as a
 * string), and the fallback covers only the remaining ids with a real `FilterValueSchema`.
 */
export const ComplianceAuditSearchSchema = z.object({
    'compliance-audit-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('auditor'),
                    operator: z.enum(['in', 'notIn']),
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('template'),
                    operator: z.literal('in'),
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z
                        .string()
                        .refine(
                            (filterId) => !RESERVED_FILTER_IDS.some((reserved) => reserved === filterId),
                            'Handled by a dedicated variant',
                        ),
                    operator: FilterOperatorSchema,
                    value: FilterValueSchema,
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

/**
 * The SUPPORT half: the parsed set, or `undefined` when any entry names something this
 * list does not offer.
 *
 * Which ids exist and which operators each one permits belongs to the filter config, so
 * it is checked against the config itself rather than re-enumerated in Zod — a second
 * list would drift the first time a filter is added or its operators are narrowed.
 * `buildWhereClause` silently skips a pair it cannot build (`status` with `contains`, an
 * id belonging to another list), which is the partial application above; one unsupported
 * entry therefore drops the whole set, matching what a malformed one already gets.
 */
export function acceptDeepLinkFilters<TWhere>(
    config: FilterConfig<TWhere>,
    filters: Filter[] | undefined,
): Filter[] | undefined {
    const isSupported = (filter: Filter) => {
        const field = config.filters.find((candidate) => candidate.id === filter.filterId);
        return !!field && getOperatorsForFieldType(field.type ?? 'text', field.operators).includes(filter.operator);
    };

    return filters?.every(isSupported) ? filters : undefined;
}
